// src/components/Proyectos/AddTareas.jsx
import React, { useState, useEffect } from "react";
import Fuse from "fuse.js";
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  CircularProgress,
  Autocomplete,
  Typography,
  Stack,
  Chip,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import {
  getTareasDefinicion,
  getResponsablesTodos,
  getDisenos,
  getMuestras,
  getMateriales,
  createTarea,
  postTareasParticipante,
} from "../../api/Proyectos/ProyectosTareasApi";
import {
  getAllProyectos,
  searchProyectos,
} from "../../api/Proyectos/proyectoApi";
import {
  pageFieldSx,
  pageHeroShellSx,
  pageContentCardSx,
  pageSectionTitleSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";

/** Excluye Admin del select de responsable / participantes. */
const isSelectableUser = (u) => {
  if (!u) return false;
  if (u.isAdmin) return false;
  const name = String(u.username || "").trim().toLowerCase();
  return name !== "admin";
};

const AddTareas = ({ open, onClose, onCreated }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fieldSx = pageFieldSx(theme);
  const accent = theme.palette.primary.main;
  const sectionCardSx = {
    ...pageContentCardSx(theme, accent),
    p: { xs: 1.5, sm: 2 },
  };

  const [loading, setLoading] = useState(true);
  const [proyectos, setProyectos] = useState([]);
  const [definiciones, setDefiniciones] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [relaciones, setRelaciones] = useState([]);
  const [relacionTipo, setRelacionTipo] = useState("Diseño");

  const [searchText, setSearchText] = useState("");
  const [projectSearch, setProjectSearch] = useState("");
  const [loadingRelaciones, setLoadingRelaciones] = useState(false);
  const [loadingProyectos, setLoadingProyectos] = useState(false);
  const [allProyectos, setAllProyectos] = useState([]);

  const [errors, setErrors] = useState({});
  const [participantes, setParticipantes] = useState([]);

  const [form, setForm] = useState({
    ProyectoId: "",
    TituloId: "",
    Descripcion: "",
    ResponsableId: "",
    RelacionTipo: "Diseño",
    RelacionId: "",
    FechaInicio: dayjs().format("YYYY-MM-DD"),
    FechaLimite: "",
    Estado: "Pending",
  });

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        setLoading(true);
        const [proy, defs, resp, disenos] = await Promise.all([
          getAllProyectos(),
          getTareasDefinicion(),
          getResponsablesTodos(),
          getDisenos(),
        ]);
        setAllProyectos(proy);
        setProyectos(proy);
        setDefiniciones(defs);
        setResponsables((resp || []).filter(isSelectableUser));
        setRelaciones(disenos);
      } catch (err) {
        console.error("❌ Error cargando datos AddTareas:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open]);

  const handleRelacionTipo = async (tipo) => {
    setRelacionTipo(tipo);
    setForm((prev) => ({ ...prev, RelacionTipo: tipo, RelacionId: "" }));
    setLoadingRelaciones(true);
    try {
      if (tipo === "Diseño") setRelaciones(await getDisenos());
      if (tipo === "Muestra") setRelaciones(await getMuestras());
      if (tipo === "Material") setRelaciones(await getMateriales());
    } catch (err) {
      console.error("❌ Error cargando relaciones:", err);
    } finally {
      setLoadingRelaciones(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    if (field === "ResponsableId") {
      setParticipantes((prev) =>
        prev.filter((u) => Number(u.id) !== Number(value)),
      );
    }
  };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!form.ProyectoId) newErrors.ProyectoId = t("ToDoList.Project");
    if (!form.TituloId) newErrors.TituloId = "Select a definition";
    if (!form.ResponsableId) newErrors.ResponsableId = "Select a responsible";
    if (!form.FechaInicio) newErrors.FechaInicio = "Select start date";
    if (!form.FechaLimite) newErrors.FechaLimite = "Select due date";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      const nueva = await createTarea(form.ProyectoId, form);
      const tareaId = nueva?.Id ?? nueva?.id;
      const responsableId = Number(form.ResponsableId);
      const extraParticipants = participantes.filter(
        (u) => Number(u.id) !== responsableId,
      );
      if (tareaId && extraParticipants.length) {
        await Promise.all(
          extraParticipants.map((u) =>
            postTareasParticipante(tareaId, { UserId: Number(u.id) }),
          ),
        );
      }
      onCreated({
        ...nueva,
        ParticipanteIds: extraParticipants.map((u) => Number(u.id)),
      });
      resetForm();
      onClose();
    } catch (err) {
      console.error("❌ Error creando tarea:", err);
    }
  };

  const fetchRelaciones = async (query) => {
    setLoadingRelaciones(true);
    let data = [];
    if (relacionTipo === "Diseño") data = await getDisenos(query);
    if (relacionTipo === "Muestra") data = await getMuestras(query);
    if (relacionTipo === "Material") data = await getMateriales(query);
    setRelaciones(data);
    setLoadingRelaciones(false);
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      if (searchText.length > 1) fetchRelaciones(searchText);
    }, 400);
    return () => clearTimeout(delay);
  }, [searchText, relacionTipo]);

  const fuseOptions = {
    keys: ["subject"],
    threshold: 0.35,
    distance: 100,
    ignoreLocation: true,
    minMatchCharLength: 2,
  };

  useEffect(() => {
    const searchTerm = projectSearch.trim();
    if (!searchTerm) {
      setProyectos(allProyectos.slice(0, 20));
      return;
    }

    const fuse = new Fuse(allProyectos, fuseOptions);
    const results = fuse.search(searchTerm, { limit: 10 });
    const localFiltered = results.map((r) => r.item);

    if (localFiltered.length === 0) {
      const controller = new AbortController();
      const timeout = setTimeout(async () => {
        if (searchTerm.length < 2) return;
        setLoadingProyectos(true);
        try {
          const result = await searchProyectos(searchTerm, {
            signal: controller.signal,
          });
          setProyectos(result);
        } catch (err) {
          if (err.name !== "CanceledError")
            console.error("❌ Error buscando proyectos:", err);
        } finally {
          setLoadingProyectos(false);
        }
      }, 300);
      return () => {
        controller.abort();
        clearTimeout(timeout);
      };
    }
    setProyectos(localFiltered);
  }, [projectSearch, allProyectos]);

  const renderHighlighted = (subject) => {
    if (!projectSearch) return subject;
    const regex = new RegExp(
      `(${projectSearch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "ig",
    );
    const parts = subject.split(regex);
    return (
      <>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <strong key={i} style={{ color: theme.palette.primary.main }}>
              {part}
            </strong>
          ) : (
            part
          ),
        )}
      </>
    );
  };

  const resetForm = () => {
    setForm({
      ProyectoId: "",
      TituloId: "",
      Descripcion: "",
      ResponsableId: "",
      RelacionTipo: "Diseño",
      RelacionId: "",
      FechaInicio: dayjs().format("YYYY-MM-DD"),
      FechaLimite: "",
      Estado: "Pending",
    });
    setErrors({});
    setParticipantes([]);
    setProjectSearch("");
    setSearchText("");
    setRelacionTipo("Diseño");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const estadoColors = {
    Done: "#2e7d32",
    Running: "#1565c0",
    Pending: "#e65100",
  };

  const estadoLabel =
    form.Estado === "Done"
      ? t("ToDoList.Done")
      : form.Estado === "Running"
        ? t("ToDoList.Running")
        : t("ToDoList.Pending");

  const selectedDefinicion =
    definiciones.find((d) => d.Id === form.TituloId)?.Nombre || "";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          m: isMobile ? 0 : 2,
          overflow: "hidden",
          bgcolor: theme.palette.background.default,
        },
      }}
    >
      <Box sx={{ ...pageHeroShellSx(theme), mb: 0, borderRadius: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: { xs: 1.5, sm: 2 } }}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(accent, 0.16),
                color: theme.palette.primary.dark,
                border: "1px solid",
                borderColor: alpha(accent, 0.28),
              }}
            >
              <TaskAltOutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontWeight: 700, letterSpacing: 1.1, lineHeight: 1.2 }}
              >
                CRM
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.15,
                  fontSize: { xs: "1.05rem", sm: "1.2rem" },
                }}
              >
                {t("ToDoList.AddTask")}
              </Typography>
              {selectedDefinicion ? (
                <Chip
                  size="small"
                  label={selectedDefinicion}
                  sx={{
                    mt: 0.5,
                    height: 22,
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    maxWidth: "100%",
                    bgcolor: alpha(accent, 0.1),
                    color: theme.palette.primary.dark,
                  }}
                />
              ) : null}
            </Box>
          </Stack>
          {isMobile ? (
            <IconButton
              onClick={handleClose}
              sx={{
                border: "1px solid",
                borderColor: alpha(theme.palette.divider, 0.8),
                bgcolor: theme.palette.background.paper,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Stack>
      </Box>

      <DialogContent
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: 2,
          pb: { xs: 3, sm: 2.5 },
        }}
      >
        {loading ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1.5}
            sx={{ py: 6 }}
          >
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {t("ToDoList.AddTask")}
            </Typography>
          </Stack>
        ) : (
          <>
            {Object.keys(errors).length > 0 ? (
              <Box sx={{ mb: 1.5 }}>
                <Typography color="error" variant="body2" fontWeight={600}>
                  {t("ToDoList.Project")}: fill required fields
                </Typography>
              </Box>
            ) : null}

            <Stack spacing={1.75}>
              <Box sx={sectionCardSx}>
                <Typography sx={pageSectionTitleSx}>
                  {t("ToDoList.Project")}
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 1.25 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={proyectos}
                    loading={loadingProyectos}
                    value={
                      proyectos.find((p) => p.id === form.ProyectoId) || null
                    }
                    inputValue={projectSearch}
                    filterOptions={(x) => x}
                    onInputChange={(_, newValue) =>
                      setProjectSearch(newValue || "")
                    }
                    getOptionLabel={(p) =>
                      p?.subject?.trim?.() || `Proyecto ${p?.id || ""}`
                    }
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    onChange={(_, newValue) =>
                      handleChange("ProyectoId", newValue?.id || "")
                    }
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Typography variant="body2">
                          {renderHighlighted(
                            option.subject || `Proyecto ${option.id}`,
                          )}
                        </Typography>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("ToDoList.Project")}
                        placeholder="Search project..."
                        error={!!errors.ProyectoId}
                        helperText={errors.ProyectoId}
                        sx={fieldSx}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingProyectos ? (
                                <CircularProgress size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.TituloId}
                    sx={fieldSx}
                  >
                    <InputLabel>{t("ToDoList.Tarea")}</InputLabel>
                    <Select
                      value={form.TituloId}
                      label={t("ToDoList.Tarea")}
                      onChange={(e) => handleChange("TituloId", e.target.value)}
                    >
                      {[...definiciones]
                        .sort((a, b) =>
                          a.Nombre.localeCompare(b.Nombre, "es", {
                            sensitivity: "base",
                          }),
                        )
                        .map((d) => (
                          <MenuItem key={d.Id} value={d.Id}>
                            {d.Nombre}
                          </MenuItem>
                        ))}
                    </Select>
                    {errors.TituloId ? (
                      <Typography variant="caption" color="error">
                        {errors.TituloId}
                      </Typography>
                    ) : null}
                  </FormControl>
                </Stack>
              </Box>

              <Box sx={sectionCardSx}>
                <Typography sx={pageSectionTitleSx}>
                  {t("ToDoList.Responsable")}
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  sx={{ mt: 1.25 }}
                >
                  <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.ResponsableId}
                    sx={fieldSx}
                  >
                    <InputLabel>{t("ToDoList.Responsable")}</InputLabel>
                    <Select
                      value={form.ResponsableId}
                      label={t("ToDoList.Responsable")}
                      onChange={(e) =>
                        handleChange("ResponsableId", e.target.value)
                      }
                    >
                      {responsables.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                          {u.username}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.ResponsableId ? (
                      <Typography variant="caption" color="error">
                        {errors.ResponsableId}
                      </Typography>
                    ) : null}
                  </FormControl>

                  <FormControl fullWidth size="small" sx={fieldSx}>
                    <InputLabel id="estado-label-add">
                      {t("ToDoList.Status")}
                    </InputLabel>
                    <Select
                      labelId="estado-label-add"
                      label={t("ToDoList.Status")}
                      value={form.Estado}
                      onChange={(e) => handleChange("Estado", e.target.value)}
                      renderValue={() => (
                        <Chip
                          size="small"
                          label={estadoLabel}
                          sx={{
                            height: 24,
                            fontWeight: 700,
                            fontSize: "0.72rem",
                            bgcolor: alpha(
                              estadoColors[form.Estado] ||
                                theme.palette.text.primary,
                              0.12,
                            ),
                            color:
                              estadoColors[form.Estado] ||
                              theme.palette.text.primary,
                            border: "1px solid",
                            borderColor: alpha(
                              estadoColors[form.Estado] ||
                                theme.palette.text.primary,
                              0.28,
                            ),
                          }}
                        />
                      )}
                    >
                      <MenuItem value="Pending">{t("ToDoList.Pending")}</MenuItem>
                      <MenuItem value="Running">{t("ToDoList.Running")}</MenuItem>
                      <MenuItem value="Done">{t("ToDoList.Done")}</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>

                <Autocomplete
                  multiple
                  fullWidth
                  size="small"
                  sx={{ ...fieldSx, mt: 1.5 }}
                  options={responsables.filter(
                    (u) => Number(u.id) !== Number(form.ResponsableId),
                  )}
                  value={participantes}
                  onChange={(_, value) => setParticipantes(value || [])}
                  getOptionLabel={(u) => u?.username || `User ${u?.id || ""}`}
                  isOptionEqualToValue={(opt, val) =>
                    Number(opt.id) === Number(val.id)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Participants"
                      placeholder="Add people who can see this task"
                    />
                  )}
                />
              </Box>

              <Box sx={sectionCardSx}>
                <Typography sx={pageSectionTitleSx}>
                  {t("ToDoList.objeto")}
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  sx={{ mt: 1.25 }}
                >
                  <FormControl fullWidth size="small" sx={fieldSx}>
                    <InputLabel>{t("ToDoList.objeto")}</InputLabel>
                    <Select
                      value={relacionTipo}
                      label={t("ToDoList.objeto")}
                      onChange={(e) => handleRelacionTipo(e.target.value)}
                    >
                      <MenuItem value="Diseño">{t("ToDoList.Diseño")}</MenuItem>
                      <MenuItem value="Muestra">{t("ToDoList.Muestra")}</MenuItem>
                      <MenuItem value="Material">
                        {t("ToDoList.Material")}
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <Autocomplete
                    fullWidth
                    size="small"
                    sx={fieldSx}
                    options={relaciones}
                    loading={loadingRelaciones}
                    onInputChange={(_, value) => setSearchText(value)}
                    getOptionLabel={(r) =>
                      r?.nombre ||
                      r?.MUESTRA ||
                      r?.NombreMaterial ||
                      `Elemento ${r?.id || ""}`
                    }
                    onChange={(_, newValue) =>
                      handleChange(
                        "RelacionId",
                        newValue?.id ||
                          newValue?.ID_MUESTRA ||
                          newValue?.Id ||
                          "",
                      )
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("ToDoList.Elemento")}
                        placeholder={t("Clientes.Buscar")}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingRelaciones ? (
                                <CircularProgress size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Stack>
              </Box>

              <Box sx={sectionCardSx}>
                <Typography sx={pageSectionTitleSx}>
                  {t("ToDoList.Descripcion")}
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 1.25 }}>
                  <TextField
                    label={t("ToDoList.Descripcion")}
                    multiline
                    minRows={3}
                    value={form.Descripcion}
                    onChange={(e) =>
                      handleChange("Descripcion", e.target.value)
                    }
                    fullWidth
                    size="small"
                    sx={fieldSx}
                  />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      name="FechaInicio"
                      fullWidth
                      size="small"
                      type="date"
                      label={t("ToDoList.Start")}
                      InputLabelProps={{ shrink: true }}
                      value={form.FechaInicio}
                      onChange={(e) =>
                        handleChange("FechaInicio", e.target.value)
                      }
                      error={!!errors.FechaInicio}
                      helperText={errors.FechaInicio}
                      sx={fieldSx}
                    />
                    <TextField
                      name="FechaLimite"
                      fullWidth
                      size="small"
                      type="date"
                      label={t("ToDoList.End")}
                      InputLabelProps={{ shrink: true }}
                      value={form.FechaLimite}
                      onChange={(e) =>
                        handleChange("FechaLimite", e.target.value)
                      }
                      error={!!errors.FechaLimite}
                      helperText={errors.FechaLimite}
                      sx={fieldSx}
                    />
                  </Stack>
                </Stack>
              </Box>
            </Stack>

            <Box
              sx={{
                position: { xs: "sticky", sm: "static" },
                bottom: 0,
                zIndex: 2,
                mt: 2,
                mx: { xs: -1.5, sm: 0 },
                px: { xs: 1.5, sm: 0 },
                pt: 1.5,
                pb: { xs: 1, sm: 0 },
                bgcolor: alpha(theme.palette.background.default, 0.92),
                backdropFilter: "blur(10px)",
                borderTop: "1px solid",
                borderColor: alpha(theme.palette.divider, 0.7),
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                justifyContent="flex-end"
                sx={{ width: "100%" }}
              >
                <Button
                  onClick={handleClose}
                  fullWidth={isMobile}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: alpha(theme.palette.divider, 0.8),
                  }}
                >
                  {t("ToDoList.Cancelar")}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  fullWidth={isMobile}
                  sx={{
                    ...pagePrimaryButtonSx(theme),
                    color: theme.palette.primary.contrastText,
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("ToDoList.AddTask")}
                </Button>
              </Stack>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddTareas;
